/**
 * Fallback competition logos when API fixture payload omits league.logo.
 * Uses API-Sports hosted PNGs (same source as UCL, EPL, etc.).
 */

const APISPORTS_LEAGUE_LOGO_BASE = 'https://media.api-sports.io/football/leagues';

/** FIFA World Cup 2026 — from frontend/public/images/leagues/worldcup.png */
export const LOCAL_LEAGUE_LOGOS = {
  worldcup: '/images/leagues/worldcup.png?v=2026',
};

const APISPORTS_LEGACY_WC_LOGO = /media\.api-sports\.io\/football\/leagues\/1\.png/i;

const LEAGUE_KEY_TO_API_ID = {
  epl: 39,
  laliga: 140,
  bundesliga: 78,
  seriea: 135,
  ligue1: 61,
  mls: 253,
  worldcup: 1,
  euro: 4,
  copaamerica: 9,
  afcon: 6,
  championsleague: 2,
  europaleague: 3,
  conferenceleague: 848,
  facup: 45,
  copadelrey: 143,
  dfbpokal: 81,
  coppaitalia: 137,
  championship: 40,
  eredivisie: 88,
  primeira: 94,
};

const LEAGUE_NAME_ALIASES = {
  'world cup': 'worldcup',
  'fifa world cup': 'worldcup',
  'uefa champions league': 'championsleague',
  'champions league': 'championsleague',
  'uefa europa league': 'europaleague',
  'europa league': 'europaleague',
  'uefa conference league': 'conferenceleague',
  'conference league': 'conferenceleague',
};

export function normalizeLeagueKey(leagueKeyOrName) {
  const raw = String(leagueKeyOrName || '').toLowerCase().trim();
  if (!raw || raw === 'unknown') return null;
  if (LEAGUE_KEY_TO_API_ID[raw]) return raw;
  if (LEAGUE_NAME_ALIASES[raw]) return LEAGUE_NAME_ALIASES[raw];
  if (raw.includes('world cup')) return 'worldcup';
  if (raw.includes('champions league')) return 'championsleague';
  return raw;
}

export function getCompetitionLogoUrl(fixture) {
  if (!fixture) return null;

  const leagueKey =
    normalizeLeagueKey(fixture.league) || normalizeLeagueKey(fixture.competition);

  if (leagueKey && LOCAL_LEAGUE_LOGOS[leagueKey]) {
    return LOCAL_LEAGUE_LOGOS[leagueKey];
  }

  const direct = fixture.competitionLogo && String(fixture.competitionLogo).trim();
  if (direct) {
    if (APISPORTS_LEGACY_WC_LOGO.test(direct) || leagueKey === 'worldcup') {
      return LOCAL_LEAGUE_LOGOS.worldcup;
    }
    return direct;
  }

  if (leagueKey === 'worldcup') {
    return LOCAL_LEAGUE_LOGOS.worldcup;
  }

  const apiId = leagueKey ? LEAGUE_KEY_TO_API_ID[leagueKey] : null;
  if (apiId && leagueKey !== 'worldcup') {
    return `${APISPORTS_LEAGUE_LOGO_BASE}/${apiId}.png`;
  }

  return null;
}

/**
 * League dropdown / nav — never use API-Sports for World Cup (id 1 = stale 2022 artwork).
 * @param {string} leagueKey
 * @param {string|number|null} [logoId] - API-Football league id from /api/leagues
 */
export function getLeagueMenuLogoUrl(leagueKey, logoId = null) {
  const key = normalizeLeagueKey(leagueKey);
  if (key === 'worldcup' || String(logoId || '').trim() === '1') {
    return LOCAL_LEAGUE_LOGOS.worldcup;
  }
  if (key && LOCAL_LEAGUE_LOGOS[key]) {
    return LOCAL_LEAGUE_LOGOS[key];
  }
  if (logoId != null && logoId !== '') {
    return `${APISPORTS_LEAGUE_LOGO_BASE}/${String(logoId).trim()}.png`;
  }
  return null;
}

/** @returns {string|null} secondary URL for img onError — never API WC for worldcup */
export function getLeagueMenuFallbackLogoUrl(leagueKey, logoId = null) {
  const key = normalizeLeagueKey(leagueKey);
  if (key === 'worldcup' || String(logoId || '').trim() === '1') {
    return null;
  }
  const local = key && LOCAL_LEAGUE_LOGOS[key];
  if (!local || !logoId) return null;
  return `${APISPORTS_LEAGUE_LOGO_BASE}/${String(logoId).trim()}.png`;
}
